"use client";

import type { CSSProperties } from "react";
import {
	FlagBanner,
	flagBannerSurfaceClassNameForPreset,
	flagBannerSurfaceClassNameForRank,
} from "../custom/flag-banner";
/** Preset gradients aligned with org entity list tiles (`entity-tile.tsx`). */
export type MagicCardFlagPreset =
	| "PICK"
	| "NEW"
	| "TREND"
	| "TOP"
	| "RANK"
	| "MUTED";

export interface MagicCardFlag {
	preset: MagicCardFlagPreset;
	/** Shown text (required for `MUTED`; optional override for other presets). */
	label?: string;
	/** For `RANK` / `TOP` when `preset` needs a numeric suffix. */
	value?: number;
	/** When `preset === "TOP"` and true, render `TOP {value}%`. */
	isPercent?: boolean;
	/** Optional override for banner surface (e.g. rarity gradient). */
	surfaceClassName?: string;
	/** Inline surface style when class utilities cannot express the fill (mythic rainbow). */
	surfaceStyle?: CSSProperties;
}

/** Delegates to `flag-banner` presets (aligned with entity tiles / podium ranks). */
export function magicCardFlagGradientClassName(flag: MagicCardFlag): string {
	const preset = flag.preset;
	if (preset === "PICK") {
		return flagBannerSurfaceClassNameForPreset("pick");
	}
	if (preset === "NEW") {
		return flagBannerSurfaceClassNameForPreset("new");
	}
	if (preset === "TREND") {
		return flagBannerSurfaceClassNameForPreset("trend");
	}
	if (preset === "RANK") {
		const raw = Math.floor(Number(flag.value));
		const n = Number.isFinite(raw) ? Math.max(0, Math.min(9999, raw)) : 0;
		return flagBannerSurfaceClassNameForRank(n);
	}
	if (preset === "TOP") {
		return flagBannerSurfaceClassNameForPreset("top");
	}
	return flagBannerSurfaceClassNameForPreset("muted");
}

export function magicCardFlagDisplayLabel(flag: MagicCardFlag): string {
	if (flag.label?.trim()) {
		return flag.label.trim();
	}
	if (flag.preset === "RANK") {
		const raw = Math.floor(Number(flag.value));
		const n = Number.isFinite(raw) ? Math.max(0, Math.min(9999, raw)) : 0;
		return `#${n}`;
	}
	if (flag.preset === "TOP") {
		if (typeof flag.value !== "number") {
			return "TOP";
		}
		const raw = Math.floor(Number(flag.value));
		const pct = flag.isPercent === true;
		const max = pct ? 99 : 999;
		const n = Number.isFinite(raw) ? Math.max(0, Math.min(max, raw)) : 0;
		return pct ? `TOP ${n}%` : `TOP ${n}`;
	}
	if (flag.preset === "MUTED") {
		return "";
	}
	return flag.preset;
}

/** Narrow column / stacked hub cards — ribbon at upper-right. Wide fan row — centred top-edge banner. */
export type MagicCardListCornerFlagPlacement = "stack" | "fan" | "menu";

/**
 * Hub / bento cards:
 * - **`stack`**: **top-right**, list-style ribbon — triangular notch + extra inset on the **left** (card interior).
 * - **`fan`**: **top-centre** — symmetrical notches left/right; positioned so the card top edge bisects the banner vertically.
 */
export function MagicCardListCornerFlag({
	flag,
	placement = "stack",
	className,
}: {
	flag: MagicCardFlag;
	placement?: MagicCardListCornerFlagPlacement;
	className?: string;
}) {
	const text = magicCardFlagDisplayLabel(flag);
	if (!text) {
		return null;
	}

	const surfaceClassName =
		flag.surfaceClassName ?? magicCardFlagGradientClassName(flag);
	const surfaceStyle = flag.surfaceStyle;

	if (placement === "fan") {
		return (
			<FlagBanner
				position="top"
				variant="horizontal"
				horizontalNotch="both"
				edgeOverflowPx={-12}
				surfaceClassName={surfaceClassName}
				surfaceStyle={surfaceStyle}
				zIndexClassName="z-[30]"
				className={className}
			>
				{text}
			</FlagBanner>
		);
	}

	if (placement === "menu") {
		// Parent `.bento-card-menu-coming-soon` owns absolute top-center placement.
		// Ribbon must stay in-flow (`positioned={false}`) so it cannot shift tile content.
		return (
			<FlagBanner
				position="top"
				variant="horizontal"
				horizontalNotch="both"
				size="compact"
				positioned={false}
				surfaceClassName={surfaceClassName}
				surfaceStyle={surfaceStyle}
				zIndexClassName="z-[30]"
				className={className}
			>
				{text}
			</FlagBanner>
		);
	}

	return (
		<FlagBanner
			position="top-right"
			variant="horizontal"
			horizontalExtraStyle={{ top: 23 }}
			surfaceClassName={surfaceClassName}
			surfaceStyle={surfaceStyle}
			zIndexClassName="z-[30]"
			className={className}
		>
			{text}
		</FlagBanner>
	);
}
